/**
 * The keys a teacher presses while flipping flashcards with one child.
 *
 * Two things make this worth a module of its own rather than a few lines in
 * the modal.
 *
 * THE VALIDATION IS LOAD-BEARING. The slideshow's handler tests pass first,
 * then fail, then almost. Bind two results to the same key and the first test
 * wins every time: a teacher who sets pass and fail both to `q` marks an entire
 * deck as PASS, silently, for a real child, and finds out at reporting time if
 * at all. Nothing in the UI prevented that before this.
 *
 * THE KEYS ARE MUSCLE MEMORY, so they belong to the TEACHER, not to a deck.
 * Having to re-set them per assessment is the complaint this exists to answer.
 */

export interface KinderKeys {
  pass: string;
  fail: string;
  /** Unset means the deck is BINARY — there is no "almost there" state. */
  almost?: string;
}

/** What the tool has always used when nothing is configured. */
export const DEFAULT_KINDER_KEYS: KinderKeys = { pass: 'q', fail: 'z' };

const clean = (v: unknown): string =>
  typeof v === 'string' && Array.from(v).length === 1 ? v.toLowerCase() : '';

/**
 * A stored value cleaned up, or null if it is not usable.
 *
 * Applied on READ as well as on write: these end up on a user document that an
 * older build, a half-finished edit, or a hand-edit could leave in any state,
 * and a deck whose keys do not work is a teacher sitting with a child pressing
 * a key that does nothing.
 */
export function normalizeKinderKeys(raw: unknown): KinderKeys | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const pass = clean(r.pass);
  const fail = clean(r.fail);
  const almost = clean(r.almost);
  if (!pass || !fail) return null;
  if (pass === fail) return null;
  // A colliding "almost" is dropped rather than failing the whole set: pass and
  // fail still work, and a binary deck is a usable deck.
  const useAlmost = almost && almost !== pass && almost !== fail;
  return useAlmost ? { pass, fail, almost } : { pass, fail };
}

/**
 * Why a proposed set cannot be saved, phrased for the teacher, or null if fine.
 *
 * Deliberately returns the REASON rather than a boolean: "that key is already
 * doing something else" is actionable and "invalid" is not.
 */
export function kinderKeyProblem(pass: string, fail: string, almost: string): string | null {
  const p = clean(pass), f = clean(fail), a = clean(almost);
  if (!p || !f) return 'Pick a key for both Pass and Fail.';
  if (p === f) return 'Pass and Fail need different keys — otherwise every card records as Pass.';
  if (a && a === p) return 'Almost is using the same key as Pass.';
  if (a && a === f) return 'Almost is using the same key as Fail.';
  return null;
}

/**
 * Which keys this assessment actually runs on.
 *
 * The teacher's global set wins when they have one; an assessment's own keys
 * are the fallback for decks made before global keys existed, so nothing
 * changes underneath a teacher until they deliberately save a global set.
 */
export function resolveKinderKeys(
  global: unknown,
  assessment: { passKey?: string; failKey?: string; almostKey?: string } | null | undefined,
): KinderKeys {
  const fromTeacher = normalizeKinderKeys(global);
  if (fromTeacher) return fromTeacher;
  const fromDeck = normalizeKinderKeys({
    pass: assessment?.passKey, fail: assessment?.failKey, almost: assessment?.almostKey,
  });
  return fromDeck || DEFAULT_KINDER_KEYS;
}

/** How a key reads in the interface. Space has to be named or it looks empty. */
export function kinderKeyLabel(key: string): string {
  if (key === ' ') return 'Space';
  return (key || '').toUpperCase();
}

/**
 * Should a keystroke be ignored by the flashcard marking handler?
 *
 * The handler is a WINDOW listener, so it sees every keystroke on the page
 * including the ones aimed at a text box. Without this, typing an observation
 * note reading "quick to answer" marks the card PASS on the 'q' and FAIL on a
 * 'z', and every arrow key used to edit the text walks the deck to another card —
 * all silently, because the note reads correctly afterwards and the wrong mark
 * looks like one the teacher made.
 *
 * Pulled out of the component and named so it can be tested, since the failure is
 * invisible in the interface and would only ever be found in a child's record.
 */
export function isTextEntryTarget(
  target: { tagName?: string; isContentEditable?: boolean } | null | undefined,
): boolean {
  if (!target) return false;
  const tag = (target.tagName || '').toUpperCase();
  if (tag === 'TEXTAREA' || tag === 'INPUT' || tag === 'SELECT') return true;
  return target.isContentEditable === true;
}
